// Analyze nested redirects and find cycles
export const analyzeNestedRedirects = (redirects) => {
  // Create a map of source -> target for quick lookup
  const redirectMap = new Map();
  redirects.forEach(redirect => {
    if (redirect.source && redirect.target) {
      redirectMap.set(redirect.source, {
        target: redirect.target,
        status: redirect.status,
        original: redirect
      });
    }
  });

  const nestedRedirects = [];
  const processedSources = new Set();

  // Find chains starting from each redirect
  redirects.forEach(redirect => {
    if (processedSources.has(redirect.source)) return;

    const chain = [];
    const visited = new Set();
    let current = redirect.source;
    let hasCycle = false;
    let cycleStart = null;

    // Follow the redirect chain
    while (current && redirectMap.has(current)) {
      if (visited.has(current)) {
        // Found a cycle
        hasCycle = true;
        cycleStart = current;
        break;
      }

      visited.add(current);
      const redirectInfo = redirectMap.get(current);
      
      chain.push({
        source: current,
        target: redirectInfo.target,
        status: redirectInfo.status,
        original: redirectInfo.original
      });

      // Check if target is also a source (continues the chain)
      current = redirectInfo.target;
      
      // Avoid infinite loops with external URLs
      if (current && !current.startsWith('/')) {
        break;
      }
    }

    // Only include chains with 2 or more redirects
    if (chain.length >= 2) {
      // Mark all sources in this chain as processed
      chain.forEach(step => processedSources.add(step.source));

      nestedRedirects.push({
        id: `chain-${nestedRedirects.length}`,
        chain,
        length: chain.length,
        hasCycle,
        cycleStart,
        finalTarget: chain[chain.length - 1].target
      });
    }
  });

  return nestedRedirects.sort((a, b) => {
    // Sort cycles first, then by chain length descending
    if (a.hasCycle && !b.hasCycle) return -1;
    if (!a.hasCycle && b.hasCycle) return 1;
    return b.length - a.length;
  });
};

// Find duplicate redirects (multiple sources pointing to same target)
export const findDuplicateRedirects = (redirects) => {
  const targetMap = new Map();

  // Group redirects by target
  redirects.forEach(redirect => {
    if (redirect.target) {
      if (!targetMap.has(redirect.target)) {
        targetMap.set(redirect.target, []);
      }
      targetMap.get(redirect.target).push(redirect);
    }
  });

  // Find targets with multiple sources
  const duplicates = [];
  targetMap.forEach((sources, target) => {
    if (sources.length > 1) {
      duplicates.push({
        target,
        sources: sources.sort((a, b) => a.source.localeCompare(b.source)),
        count: sources.length
      });
    }
  });

  return duplicates.sort((a, b) => {
    // Sort by count descending, then by target alphabetically
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return a.target.localeCompare(b.target);
  });
};

// Get statistics for nested redirects
export const getNestedRedirectStats = (nestedRedirects) => {
  const totalChains = nestedRedirects.length;
  const totalCycles = nestedRedirects.filter(chain => chain.hasCycle).length;
  const longestChain = nestedRedirects.reduce((max, chain) => 
    Math.max(max, chain.length), 0
  );
  const averageChainLength = totalChains > 0 
    ? nestedRedirects.reduce((sum, chain) => sum + chain.length, 0) / totalChains 
    : 0;

  return {
    totalChains,
    totalCycles,
    longestChain,
    averageChainLength: Math.round(averageChainLength * 10) / 10
  };
};

// Get statistics for duplicate redirects
export const getDuplicateRedirectStats = (duplicates) => {
  const totalDuplicateTargets = duplicates.length;
  const totalRedirectsInDuplicates = duplicates.reduce((sum, dup) => sum + dup.count, 0);
  const maxDuplicatesForTarget = duplicates.reduce((max, dup) => 
    Math.max(max, dup.count), 0
  );

  return {
    totalDuplicateTargets,
    totalRedirectsInDuplicates,
    maxDuplicatesForTarget
  };
}; 